/** What the generator knows while it lays features out, and the depth curves it reads (docs/spec/03 §4). */

import type { Rng } from '../../sim/rng';
import type { Cell, Rect, Sketch } from './sketch';
import { rectDistance } from './sketch';

/** No creature within this many cells (king moves) of the inbox. */
export const INBOX_CLEARANCE = 5;

export type FeatureId =
  | 'room' | 'wall' | 'shelf' | 'cache'
  | 'firefly' | 'butterfly' | 'amoeba' | 'magic'
  | 'expand' | 'slime' | 'vault' | 'clocks';

export interface Plan {
  readonly depth: number;
  /** A 20 x 12 bonus cave: features come smaller. */
  readonly bonus: boolean;
  readonly inbox: Cell;
  /** Placed before the features in a full cave, after them in a bonus cave. */
  exit: Cell | undefined;
  /** Where fireflies and butterflies start: the amoeba keeps its distance. */
  readonly creatures: Cell[];
  /** Vault doors placed whose keys still need a home. */
  readonly keys: { readonly color: number; readonly door: Cell }[];
  readonly placed: FeatureId[];
  amoebaTime: number;
  magicWallTime: number;
}

/** A feature draws itself somewhere free and claims its ground; false if it found no room. */
export type Build = (sk: Sketch, rng: Rng, plan: Plan) => boolean;

/** A spot a creature may live in: more than `INBOX_CLEARANCE` cells from the inbox. */
export function awayFromInbox(plan: Plan): (r: Rect) => boolean {
  return (r) => rectDistance(r, plan.inbox) > INBOX_CLEARANCE;
}

/** How far along the difficulty curve a depth is: 0 at depth 1, 1 from depth 20 on. */
export function progress(depth: number): number {
  return Math.min(Math.max(depth - 1, 0), 19) / 19;
}

/** Milliseconds per scan: 160 at depth 1 down to 110 at depth 20, in steps of 5. */
export function tickFor(depth: number): number {
  return 5 * Math.round((160 - 50 * progress(depth)) / 5);
}

/** How generous the clock is over the route estimate: 2.5 at depth 1 down to 1.5 at depth 20. */
export function slackFor(depth: number): number {
  return 2.5 - progress(depth);
}

/** The share of the reachable diamonds the quota asks for: 40 % at depth 1 up to 70 % at 20. */
export function quotaShare(depth: number): number {
  return 0.4 + 0.3 * progress(depth);
}

/** Points per diamond before and after the quota: 10/15 at depth 1, rising (Classic A to P spans 10/15 to 25/50). */
export function valuesFor(depth: number): { diamondValue: number; extraValue: number } {
  const d = Math.max(1, depth) - 1;
  const diamondValue = Math.min(40, 10 + Math.floor(d * 0.8));
  return { diamondValue, extraValue: Math.min(90, diamondValue + 5 + Math.floor(d * 1.2)) };
}

/** A random integer in [lo, hi]. */
export function between(rng: Rng, lo: number, hi: number): number {
  return lo + rng.int(hi - lo + 1);
}

/** True `percent` times in a hundred. */
export function chance(rng: Rng, percent: number): boolean {
  return rng.int(100) < percent;
}
